// src/components/artist/ArtistMemberListModal.js
import React, { useMemo } from 'react';
import '../../../css/artist/chats/ArtistMemberListModal.css';
import MemberListModalLayout from '../../common/chat/members/MemberListModalLayout';
import MemberListModalContent from '../../common/chat/members/MemberListModalContent';
import { ArtistMemberProfileModal } from '../../common/chat/members/memberProfileModalVariants';
import {
  parsePartIds,
  pickFirstString,
} from '../../common/chat/members/memberListUtils';
import {
  buildGroupArtistSectionsForModal,
  resolveEventResponsibleAdminsForModal,
} from '../../common/chat/members/memberModalSharedDataUtils';
import { resolveGroupArtistPartValues } from '../../common/chat/members/groupArtistPartUtils';
import {
  formatAdminName,
} from '../../common/chat/members/memberModalProfileUtils';
import useMemberModalEventState from '../../common/chat/members/useMemberModalEventState';
import useEventMemberModalData from '../../common/chat/members/useEventMemberModalData';
import useGroupMemberModalData from '../../common/chat/members/useGroupMemberModalData';
import useMemberListModalPresentation from '../../common/chat/members/useMemberListModalPresentation';

const API_URL = process.env.REACT_APP_API_URL;
const ARTIST_PROFILE_IMAGE_PATH = process.env.REACT_APP_ARTIST_PROFILE_IMAGE_PATH;
const ADMIN_PROFILE_IMAGE_PATH = process.env.REACT_APP_ADMIN_PROFILE_IMAGE_PATH;

const EMPTY_LIST = Object.freeze([]);

/**
 * ArtistMemberListModal（管理者モーダルを踏襲／リンクは削除）
 * - 本契約ステータス（agreed / billed）のみ表示
 * - 決定済みはイベント側のパート順、未決定はマスタ順→氏名
 * - 右側の表示：決定済みは割当パートの略称、未決定はメインパートの略称
 */
export default function ArtistMemberListModal({
  isOpen,
  open,
  onClose,
  eventId,
  groupThreadId,
  groupThread = null,
  partsList = EMPTY_LIST,
  assignedAdmins = EMPTY_LIST,
  conciergePlanner = null,
  isConciergeEvent = false,
  membersVisible = true,
  currentEventMembersVisible = true,
  isUnifiedEventGroupChat = false,
}) {
  const actuallyOpen = typeof isOpen === 'boolean' ? isOpen : open;
  const isGroupMode = Boolean(groupThreadId);
  const shouldFetchCurrentEventMembers =
    membersVisible !== false && currentEventMembersVisible !== false;

  const {
    loading: eventLoading,
    participantSnapshot,
    parts: eventParts,
    castingInfo,
    progress,
  } = useEventMemberModalData({
    open: actuallyOpen,
    enabled: !isGroupMode,
    apiUrl: API_URL,
    eventId,
    participantsUrl: `${API_URL}/api/artist/event-chat/participants/${eventId}`,
    includeCastingInfo: shouldFetchCurrentEventMembers,
    includeCastingProgress: shouldFetchCurrentEventMembers,
    partsList,
    resetOnDisable: true,
    resetOnClose: true,
    errorLabel: 'ArtistMemberListModal fetch error',
  });
  const {
    loading: groupLoading,
    parts: groupParts,
    thread: fetchedGroupThread,
  } = useGroupMemberModalData({
    open: actuallyOpen,
    enabled: isGroupMode,
    apiUrl: API_URL,
    groupThreadId,
    partsList,
    mode: 'artist',
    resetOnDisable: true,
    resetOnClose: true,
    errorLabel: 'ArtistMemberListModal fetch error',
  });
  const loading = isGroupMode ? groupLoading : eventLoading;
  const parts = isGroupMode ? groupParts : eventParts;
  const effectiveGroupThread = isGroupMode ? (fetchedGroupThread || groupThread || null) : null;
  const groupThreadMembers = useMemo(
    () => ({
      assignees: Array.isArray(effectiveGroupThread?.assignees) ? effectiveGroupThread.assignees : [],
      artists: Array.isArray(effectiveGroupThread?.artists) ? effectiveGroupThread.artists : [],
    }),
    [effectiveGroupThread]
  );

  const {
    partsById,
    memberPartScopeEventUuid,
    setMemberPartScopeEventUuid,
    memberPartScopeTabs,
    resolveAdminMembershipLabel,
    resolveConciergeMembershipLabel,
    resolveArtistMembershipLabel,
    decidedSections,
    undecidedList,
  } = useMemberModalEventState({
    open: actuallyOpen,
    eventId,
    participantSnapshot,
    parts,
    castingInfo,
    progress,
    artistsVisibleOnly: true,
    parsePartIdsFn: parsePartIds,
    pickFirstStringFn: pickFirstString,
    useSnapshotArtistsForUnified: !isGroupMode,
    isUnifiedEventGroupChat,
  });

  const groupArtistSections = useMemo(() => {
    if (!isGroupMode) return [];
    const artists = Array.isArray(groupThreadMembers?.artists) ? groupThreadMembers.artists : [];
    if (!artists.length) return [];
    return buildGroupArtistSectionsForModal({
      artists,
      partsById,
      parsePartIds,
      pickFirstStringFn: pickFirstString,
      resolvePartIdsFn: resolveGroupArtistPartValues,
    }).map((section) => ({
      ...section,
      titleShort: '',
    }));
  }, [groupThreadMembers?.artists, isGroupMode, partsById]);

  const responsibleAdmins = useMemo(() => {
    if (isGroupMode) {
      const source = groupThreadMembers?.assignees;
      if (!Array.isArray(source)) return [];
      const seen = new Set();
      return source.filter((admin) => {
        if (!admin) return false;
        const rawKey = admin.admin_user_id ?? admin.id ?? admin.username ?? admin.display_name;
        if (rawKey === null || rawKey === undefined || rawKey === '') return true;
        const key = String(rawKey);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    }
    return resolveEventResponsibleAdminsForModal({
      participantSnapshot,
      assignedAdmins,
    });
  }, [assignedAdmins, groupThreadMembers?.assignees, isGroupMode, participantSnapshot]);

  const {
    profileNode,
    conciergeMembers,
    avatarStyle,
    avatarInitial,
    adminAvatarStyle,
    adminInitial,
    openAdminProfile,
    openArtistProfile,
  } = useMemberListModalPresentation({
    apiUrl: API_URL,
    artistProfilePath: ARTIST_PROFILE_IMAGE_PATH,
    adminProfilePath: ADMIN_PROFILE_IMAGE_PATH,
    isConciergeEvent,
    conciergePlanner,
    resolveArtistMembershipLabel,
    resolveAdminMembershipLabel,
    resolveConciergeMembershipLabel,
    variant: 'artist',
    allowArtistLink: false,
  });

  if (!actuallyOpen) return null;

  const showArtistMembers = isGroupMode ? true : membersVisible !== false;
  const isAllMemberPartScope = isGroupMode ? true : String(memberPartScopeEventUuid) === 'all';
  return (
    <MemberListModalLayout
      onClose={onClose}
      loading={loading}
      loadingLabel="読み込み中..."
      loadingInsideBody
      profileNode={profileNode}
      profilePlacement="inside"
    >
      <MemberListModalContent
        admins={responsibleAdmins}
        onAdminClick={openAdminProfile}
        adminKeyResolver={(admin, idx) => (
          admin.admin_user_id || admin.id || admin.username || `${formatAdminName(admin)}-${idx}`
        )}
        formatAdminName={formatAdminName}
        resolveAdminMembershipLabel={resolveAdminMembershipLabel}
        adminAvatarStyle={adminAvatarStyle}
        adminInitial={adminInitial}
        plannerMembers={conciergeMembers}
        showPlanner={!isGroupMode && isConciergeEvent}
        onPlannerClick={(planner) => {
          if (planner?.payload) {
            openArtistProfile(planner.payload);
          }
        }}
        avatarStyle={avatarStyle}
        avatarInitial={avatarInitial}
        responsibleEmptyText="担当者が設定されていません"
        plannerEmptyText="企画者が設定されていません"
        showPartScopeTabs={!isGroupMode}
        partScopeTabs={memberPartScopeTabs}
        activePartScopeValue={memberPartScopeEventUuid}
        onChangePartScope={setMemberPartScopeEventUuid}
        partScopeKeyPrefix="artist-member-scope"
        showArtistMembers={showArtistMembers}
        isGroupMode={isGroupMode}
        groupArtistSections={groupArtistSections}
        decidedSections={decidedSections}
        undecidedList={undecidedList}
        isAllMemberPartScope={isAllMemberPartScope}
        onArtistClick={openArtistProfile}
        emptyText="参加メンバーがいません"
        hiddenText="参加メンバー決定後公開"
      />
    </MemberListModalLayout>
  );
}

export const MemberProfileModal = ArtistMemberProfileModal;
